import type { EnrolmentActionTarget } from './enrolmentActionTarget';
import { djs } from '@duke-hq/djs';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { StudentCourseEnrolmentStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/StudentCourseEnrolmentStatus.schema';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AdminNoteCategory } from '@duke-hq/svc-api-db';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  enrolment: EnrolmentActionTarget;
};

function PauseEnrolmentModal({ enrolment, isOpen, onClose }: Props): React.ReactElement {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();
  const [isResumeImmediately, setIsResumeImmediately] = useState<boolean>(true);
  const pauseEnrolment = trpcAdmin.enrolment.pauseCourseEnrolment.useMutation({
    async onSuccess() {
      toast({
        status: 'success',
        title: t`Course enrolment has been paused`,
      });

      await Promise.all([
        utils.enrolment.list.invalidate(undefined, { refetchType: 'active' }),
        utils.enrolment.byIdWithResourceStatus.refetch({
          enrolmentId: enrolment.id,
        }),
      ]);

      resetFieldsAndClose();
    },
    onError(error) {
      toast({
        status: 'error',
        title: t`An error occurred while pausing the course enrolment`,
        description: error?.message ?? t`An error occurred while pausing the course`,
      });
    },
  });

  const resumeEnrolment = trpcAdmin.enrolment.resumeCourseEnrolment.useMutation({
    async onSuccess() {
      toast({
        status: 'success',
        title: t`Course enrolment has been resumed`,
      });

      await Promise.all([
        utils.enrolment.list.invalidate(undefined, { refetchType: 'active' }),
        utils.enrolment.byIdWithResourceStatus.refetch({
          enrolmentId: enrolment.id,
        }),
      ]);

      resetFieldsAndClose();
    },
    onError() {
      toast({
        status: 'error',
        title: t`An error occurred while resuming the course enrolment`,
      });
    },
  });

  const defaultValues = {
    resumeAt: djs().add(1, 'week').format('YYYY-MM-DD'),
    notifyStudentAndConsultant: true,
    text: '',
    category: undefined,
  } as const;

  const methods = useForm<{
    category: AdminNoteCategory;
    text: string;
    notifyStudentAndConsultant: boolean;
    resume_at: string;
  }>({
    defaultValues,
  });

  const {
    setValue,
    clearErrors,
    handleSubmit,
    reset,
    register,
    control,
    formState: { errors },
    watch,
  } = methods;

  const resetFieldsAndClose = (): void => {
    reset();
    clearErrors();
    onClose();
  };

  const onPause = async (data: {
    text: string;
    notifyStudentAndConsultant: boolean;
    resume_at: string;
  }): Promise<void> => {
    await pauseEnrolment.mutateAsync({
      enrolmentId: enrolment.id,
      notes: data.text,
      notifyStudentAndConsultant: data.notifyStudentAndConsultant,
      resumeAt: new Date(data.resume_at || djs().add(1, 'week').format('YYYY-MM-DD')),
    });
  };

  const onResume = async (data: {
    resume_at: string;
    notifyStudentAndConsultant: boolean;
  }): Promise<void> => {
    const courseEnrolmentScheduleAdjustment = enrolment?.CourseEnrolmentScheduleAdjustments?.[0];
    if (!courseEnrolmentScheduleAdjustment?.id) {
      return;
    }

    await resumeEnrolment.mutateAsync({
      enrolmentId: enrolment.id,
      resumeAt: isResumeImmediately ? new Date() : new Date(data.resume_at),
      scheduleAdjustmentId: courseEnrolmentScheduleAdjustment.id,
      notifyStudentAndConsultant: data.notifyStudentAndConsultant,
    });
  };

  const notifyStudentAndConsultant = watch('notifyStudentAndConsultant');

  const resumeDate = watch('resume_at')
    ? djs(watch('resume_at')).format('DD/MM/YYYY')
    : djs().add(1, 'week').format('DD/MM/YYYY');

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={() => {
        resetFieldsAndClose();
      }}
    >
      <ModalOverlay />
      {enrolment.status === StudentCourseEnrolmentStatusSchema.enum.IN_PROGRESS ? (
        <ModalContent
          as={'form'}
          padding={4}
          minH={'300px'}
          onSubmit={handleSubmit(onPause)}
          noValidate
        >
          <ModalHeader>
            <Trans>Pause enrolment</Trans>
            <ModalCloseButton margin={4} />
          </ModalHeader>

          <ModalBody pb={6} h="full">
            <FormProvider {...methods}>
              <VStack gap="8px" w="full" py="8px">
                <HStack w="full" justify="space-between" align="start" gap="10px">
                  <FormControl isInvalid={!!errors.resume_at} isRequired>
                    <FormLabel htmlFor="resume_at">
                      <Trans>Resume at</Trans>
                    </FormLabel>
                    <Input
                      id="resume_at"
                      type="date"
                      min={djs().add(1, 'week').format('YYYY-MM-DD')}
                      defaultValue={djs().add(1, 'week').format('YYYY-MM-DD')}
                      _disabled={{
                        opacity: 0.6,
                        cursor: 'not-allowed',
                        backgroundColor: 'gray.100',
                      }}
                      {...register('resume_at', {
                        required: t`Pause ends date is required`,
                        validate: (value) => {
                          const selectedDate = djs(value).startOf('day');
                          const minDate = djs().add(1, 'week').startOf('day');

                          if (!selectedDate.isValid()) {
                            return t`Please enter a valid date.`;
                          }

                          if (selectedDate.isBefore(minDate)) {
                            return t`Please select a date that is at least one week after today.`;
                          }

                          return true;
                        },
                      })}
                    />
                    <FormErrorMessage>{errors.resume_at?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
              </VStack>

              <VStack>
                <FormControl isInvalid={!!errors.text} isRequired>
                  <FormLabel htmlFor="text">
                    <Trans>Notes</Trans>
                  </FormLabel>
                  <Controller
                    name="text"
                    rules={{
                      required: t`Notes required`,
                    }}
                    control={control}
                    render={({ field }) => (
                      <Textarea id="text" height="auto" rows={5} mb={2} {...field} />
                    )}
                  />
                  <FormErrorMessage mb="2">{errors.text?.message}</FormErrorMessage>
                </FormControl>
              </VStack>

              <VStack mb="1">
                <FormControl>
                  <Controller
                    name="notifyStudentAndConsultant"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        name="notifyStudentAndConsultant"
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                        isChecked={notifyStudentAndConsultant}
                      >
                        <Text textStyle="sm" color="fg.muted">
                          <Trans>Notify student and consultant</Trans>
                        </Text>
                      </Checkbox>
                    )}
                  />
                </FormControl>
              </VStack>
            </FormProvider>

            <Alert status="info">
              <HStack>
                <AlertIcon />
              </HStack>
              <AlertDescription>
                <Text textStyle="sm" color="fg.muted" align="left">
                  <Trans>
                    This enrolment will automatically resume on the {resumeDate} and the student
                    schedule will be rebuilt.
                  </Trans>
                </Text>
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter gap={4}>
            <Button type="button" variant="ghost" colorScheme="black" onClick={resetFieldsAndClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit">
              <Trans>Pause this enrolment</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      ) : enrolment.status === StudentCourseEnrolmentStatusSchema.enum.PAUSED ? (
        <ModalContent
          as={'form'}
          padding={4}
          minH={'300px'}
          onSubmit={handleSubmit(onResume)}
          noValidate
        >
          <ModalHeader>
            <Trans>Resume enrolment</Trans>
            <ModalCloseButton margin={4} />
          </ModalHeader>
          <ModalBody pb={6} h="full">
            <VStack alignItems="start">
              <ButtonGroup spacing={0} isAttached>
                <Button
                  colorScheme={isResumeImmediately ? 'brand' : 'gray'}
                  variant={isResumeImmediately ? 'solid' : 'outline'}
                  onClick={() => setIsResumeImmediately(true)} // Set to true for "Immediately"
                >
                  <Trans>Immediately</Trans>
                </Button>
                <Button
                  colorScheme={!isResumeImmediately ? 'brand' : 'gray'}
                  variant={!isResumeImmediately ? 'solid' : 'outline'}
                  onClick={() => {
                    setIsResumeImmediately(false);
                    setValue(
                      'resume_at',
                      enrolment.CourseEnrolmentScheduleAdjustments.length > 0
                        ? djs(
                            enrolment?.CourseEnrolmentScheduleAdjustments[0]?.scheduled_start_date,
                          ).format('YYYY-MM-DD')
                        : djs().format('YYYY-MM-DD'),
                      { shouldValidate: true }, // Ensures validation runs immediately
                    );
                  }}
                >
                  <Trans>On date</Trans>
                </Button>
              </ButtonGroup>
            </VStack>

            <FormProvider {...methods}>
              <VStack gap="8px" w="full" py="8px">
                {!isResumeImmediately ? (
                  <HStack w="full" justify="space-between" align="start" gap="10px">
                    <FormControl
                      isInvalid={!!errors.resume_at}
                      isRequired={isResumeImmediately ? false : true}
                    >
                      <FormLabel htmlFor="resume_at">
                        <Trans>Resume at</Trans>
                      </FormLabel>
                      <Input
                        id="resume_at"
                        type="date"
                        min={djs().format('YYYY-MM-DD')}
                        defaultValue={
                          enrolment.CourseEnrolmentScheduleAdjustments.length > 0
                            ? djs(
                                enrolment?.CourseEnrolmentScheduleAdjustments[0]
                                  ?.scheduled_start_date,
                              ).format('YYYY-MM-DD')
                            : djs().format('YYYY-MM-DD')
                        }
                        _disabled={{
                          opacity: 0.6,
                          cursor: 'not-allowed',
                          backgroundColor: 'gray.100',
                        }}
                        {...register('resume_at', {
                          required: isResumeImmediately ? false : t`Resume at date is required`,
                          validate: (value) => {
                            if (!djs(value).isValid()) {
                              return t`Please enter a valid date.`;
                            }
                            return true;
                          },
                        })}
                        isDisabled={isResumeImmediately}
                      />
                      <FormErrorMessage>{errors.resume_at?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>
                ) : null}
              </VStack>

              <VStack my={8}>
                <FormControl>
                  <Controller
                    name="notifyStudentAndConsultant"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        name="notifyStudentAndConsultant"
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                        isChecked={notifyStudentAndConsultant}
                      >
                        <Text textStyle="sm" color="fg.muted">
                          <Trans>Notify student and consultant</Trans>
                        </Text>
                      </Checkbox>
                    )}
                  />
                </FormControl>
              </VStack>
            </FormProvider>

            <Alert status="info">
              <HStack>
                <AlertIcon />
              </HStack>
              <AlertDescription>
                <Text textStyle="sm" color="fg.muted" align="left">
                  {isResumeImmediately ? (
                    <Trans>
                      This enrolment will automatically resume today and the student schedule will
                      be rebuilt.
                    </Trans>
                  ) : (
                    <>
                      <Trans>
                        This enrolment will be resumed and the student schedule updated to start
                        on{' '}
                      </Trans>{' '}
                      {watch('resume_at')
                        ? djs(watch('resume_at')).format('DD/MM/YYYY')
                        : djs().add(1, 'week').format('DD/MM/YYYY')}{' '}
                    </>
                  )}
                </Text>
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter gap={4}>
            <Button type="button" variant="ghost" colorScheme="black" onClick={resetFieldsAndClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit">
              <Trans>Resume this enrolment</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      ) : null}
    </Modal>
  );
}

export default PauseEnrolmentModal;
